<template>
  <div class="max-w-screen-2xl mx-auto">
    <Message severity="info" closable class="mb-4 max-w-max">
      Report Downloads have moved to the new page.
      <router-link
        :to="{ name: REPORT_ROUTE_NAMES.DOWNLOADS }"
        class="text-fg-link-primary underline"
      >
        Click here to view.
      </router-link>
    </Message>
    <div class="font-heading font-semibold text-4xl mb-3">Performances</div>
    <ReportsTableFilter @search="searchQuery = $event" @filterByDateRange="dateFilter = $event" />
    <DataTable id="report-data-table" :columns="reportColumns" :items="filteredPerformances" />
  </div>
</template>

<script lang="ts">
import * as _ from 'lodash-es'
import moment from 'moment'
import { mapStores } from 'pinia'
import Message from 'primevue/message'
import { defineComponent } from 'vue'

import DataTable from '@/common/components/organisms/DataTable.vue'
import ReportsTableFilter from '@/components/ReportsTableFilter.vue'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { REPORT_ROUTE_NAMES } from '@/modules/report/constants/ReportRouteNames'
import { useSettingStore } from '@/stores/setting'
import { cleanString, sortBy } from '@/utils/utils'

import { PerformanceQueryParams, PerformanceReportRead } from '@generated/types'

interface DisplayReport extends PerformanceReportRead {
  netFace: number
  date: string
  time: string
  percentSold: number
}

export default defineComponent({
  name: 'ReportsPerformances',
  components: {
    Message,
    ReportsTableFilter,
    DataTable,
  },
  data() {
    return {
      reports: [] as PerformanceReportRead[],
      searchQuery: '',
      dateFilter: {
        startDate: '',
        endDate: '',
      },
    }
  },
  methods: {
    async fetchReports() {
      try {
        const params = {} as PerformanceQueryParams
        if (this.dateFilter.startDate && this.dateFilter.endDate) {
          params.start_date = this.dateFilter.startDate
          params.end_date = this.dateFilter.endDate
        }
        this.reports = await this.performanceStore.getPerformanceReports(params)
        sortBy(this.reports, '-datetime')
      } catch (error) {
        console.error(error)
      }
    },
    averageTicketsPerDay(performanceReport: PerformanceReportRead): number {
      const initialDateOfPerformance: moment.Moment =
        performanceReport.publish_datetime &&
        performanceReport.publish_datetime !== this.settingStore.nowDateTime()
          ? moment(performanceReport.publish_datetime)
          : moment(performanceReport.created)

      let daysOnSale: number = 1
      if (
        this.settingStore
          .parseMoment(performanceReport.datetime)
          .isAfter(this.settingStore.nowDateTime())
      ) {
        daysOnSale = this.settingStore
          .parseMoment(this.settingStore.nowDateTime())
          .diff(initialDateOfPerformance, 'days')
      } else {
        daysOnSale = this.settingStore
          .parseMoment(performanceReport.datetime)
          .diff(this.settingStore.parseMoment(performanceReport.publish_datetime as string), 'days')
      }

      let daysToSellOut: number = moment(performanceReport.datetime).diff(
        moment(initialDateOfPerformance.format()),
        'days',
      )
      return _.round(
        performanceReport.booked_guests / (performanceReport.sold ? daysToSellOut : daysOnSale),
        1,
      )
    },
  },
  watch: {
    dateFilter: {
      handler: 'fetchReports',
      deep: true,
    },
  },
  computed: {
    REPORT_ROUTE_NAMES() {
      return REPORT_ROUTE_NAMES
    },
    ...mapStores(usePerformanceStore, useSettingStore),

    filteredPerformances(): DisplayReport[] {
      return this.reports
        .map(report => ({
          ...report,
          showName: report.show.name,
          date: this.settingStore.dateIsoStr(report.datetime),
          time: this.settingStore.timeStr(report.datetime),
          netFace: +report.net_face,
          percentSold: +((report.booked_tables * 100) / (report.table_count || 1)).toFixed(0),
          ticketsPerDay: this.averageTicketsPerDay(report),
        }))
        .filter(report =>
          this.searchQuery ? cleanString(report.showName).includes(this.searchQuery) : true,
        )
    },

    reportColumns() {
      return [
        { field: 'showName', label: 'Show' },
        { field: 'date', label: 'Date' },
        { field: 'time', label: 'Time' },
        {
          field: 'netFace',
          label: 'Net Sales',
          classes: 'text-right relative',
          tooltip: 'Tickets - Discounts - Refunds',
          mask: (value: string) => this.settingStore.currency(value).split('.')[0],
        },
        { field: 'booked_guests', label: 'Tickets', classes: 'text-right' },
        { field: 'ticketsPerDay', label: 'Tickets/Day', classes: 'text-right' },
        { field: 'percentSold', label: '% Sold', classes: 'text-right' },
      ]
    },
  },
})
</script>
